// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { WEIGHT_STEPS } from '@/components/GameWeightControl';
import type { GameMeritLadder, MeritLaddersByGameId } from '@/features/game-definitions/merits';

const PRESET_WEIGHTS: ReadonlySet<number> = new Set(WEIGHT_STEPS);

/**
 * The merit ladder every game pays, per weight, from GET /api/game-merit-ladders.
 *
 * The response covers every preset weight, so tapping the weight chip picks a row that is
 * already here and never refetches. `extraWeights` are the weights a saved tour or round may
 * carry that are not presets (a definition default of 40, an older step); the endpoint resolves
 * those too, and only a new one triggers a fetch. `roundCount` is folded into the tour-long games'
 * ladders and projects every other game's totals over the tour. `meritBoostPercent` is the round's
 * boost: the rows stay keyed by each game's own weight, and the server pays them boosted.
 */
export function useGameMeritLadders(
  apiBaseUrl: string,
  token: string | null | undefined,
  playerCount: number,
  roundCount = 1,
  extraWeights: ReadonlyArray<number> = [],
  meritBoostPercent = 100,
): MeritLaddersByGameId {
  const [laddersByGameId, setLaddersByGameId] = useState<MeritLaddersByGameId>({});
  const weightsQuery = useMemo(
    () =>
      Array.from(new Set(extraWeights.filter((weight) => Number.isInteger(weight) && !PRESET_WEIGHTS.has(weight))))
        .sort((a, b) => a - b)
        .join(','),
    [extraWeights],
  );

  useEffect(() => {
    if (!token || playerCount <= 0) {
      setLaddersByGameId({});
      return;
    }

    let cancelled = false;
    const fetchLadders = async () => {
      try {
        const query = `playerCount=${playerCount}&roundCount=${Math.max(1, roundCount)}${weightsQuery ? `&weights=${weightsQuery}` : ''}${meritBoostPercent > 100 ? `&meritBoost=${meritBoostPercent}` : ''}`;
        const response = await fetch(`${apiBaseUrl}/api/game-merit-ladders?${query}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          if (!cancelled) setLaddersByGameId({});
          return;
        }
        const payload = (await response.json()) as GameMeritLadder[];
        if (cancelled) return;
        const next: MeritLaddersByGameId = {};
        (payload ?? []).forEach((entry) => {
          if (entry?.gameDefinitionId && entry.laddersByWeight) {
            next[entry.gameDefinitionId] = entry;
          }
        });
        setLaddersByGameId(next);
      } catch {
        if (!cancelled) setLaddersByGameId({});
      }
    };

    fetchLadders();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, meritBoostPercent, playerCount, roundCount, token, weightsQuery]);

  return laddersByGameId;
}
